"use client";
import {
  type NotificationAction,
  NotificationActionType,
  webPaths,
} from "@repo/common/notifications";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@repo/ui";
import { Button } from "@repo/ui";
import { ScrollArea, Skeleton } from "@repo/ui";
import { formatDistanceToNow } from "date-fns";
import { Archive, Check, Settings } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import React from "react";

import { useMenuFocusReturn } from "@/hooks/use-menu-focus-return";
import { api } from "@/lib/trpc/react";
import { cn } from "@/lib/utils";

import { NotificationIcon } from "./notification-icon";

// Type definition for notifications (used throughout the component)
interface Notification {
  id: string;
  type: string;
  message: string;
  read: boolean;
  createdAt: Date | string;
  data?: {
    richBody?: string;
    action?: NotificationAction;
    invitationId?: string;
    organizationSlug?: string;
    projectSlug?: string;
    studyId?: string;
    [key: string]: unknown;
  };
}

/**
 * Renders notification message content
 * Uses richBody if available, otherwise falls back to plain body with markdown-like formatting
 */
function renderNotificationContent(notification: Notification) {
  // Use richBody if available (structured markdown content)
  const content = notification.data?.richBody || notification.message;

  if (!content) {
    return null;
  }

  // Simple markdown-like rendering for **bold** text
  const parts = content.split(/(\*\*.*?\*\*)/g);

  /* eslint-disable react/no-array-index-key -- Parts are derived from string split with no stable IDs */
  return parts.map((part: string, index: number) => {
    // Check if this is a bold segment (wrapped in **)
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={`bold-${index}-${part.slice(2, 12)}`}>
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <span key={`text-${index}-${part.slice(0, 10)}`}>{part}</span>;
  });
  /* eslint-enable react/no-array-index-key */
}

/**
 * Get the action configuration for a notification
 * Uses the structured action from data if available, otherwise falls back to type-based logic
 */
function getNotificationAction(notification: Notification): NotificationAction {
  // Use structured action if available
  if (notification.data?.action) {
    return notification.data.action;
  }

  // Fallback to type-based action mapping for backwards compatibility
  switch (notification.type) {
    case "INTERVIEW_INVITATION_SENT":
      return {
        type: NotificationActionType.NAVIGATE,
        webPath: notification.data?.invitationId
          ? `/invitations/${notification.data.invitationId}/accept`
          : "/me",
      };

    case "ORGANIZATION_INVITATION_CREATED":
      return {
        type: NotificationActionType.NAVIGATE,
        webPath: `/accept-invitation/${notification.data?.invitationId || ""}`,
      };

    case "ORGANIZATION_INVITATION_ACCEPTED":
      return {
        type: NotificationActionType.NAVIGATE,
        webPath: notification.data?.organizationSlug
          ? `/${notification.data.organizationSlug}/settings/members`
          : "/home",
      };

    case "ORGANIZATION_INVITATION_REVOKED":
      return { type: NotificationActionType.DISMISS };

    case "INTERVIEW_EARNINGS_POSTED":
    case "INTERVIEW_APPROVED":
    case "INTERVIEW_AUTO_APPROVED":
    case "INTERVIEW_REJECTED":
    case "PAYMENT_CLAWED_BACK":
    case "BALANCE_NEGATIVE":
      return {
        type: NotificationActionType.NAVIGATE,
        webPath: webPaths.earnings(),
      };

    case "BUDGET_THRESHOLD_REACHED":
    case "STUDY_APPROVAL_DECIDED":
    case "STUDY_STARTED":
    case "STUDY_COMPLETED":
      return {
        type: NotificationActionType.NAVIGATE,
        webPath:
          notification.data?.organizationSlug &&
          notification.data?.projectSlug &&
          notification.data?.studyId
            ? `/${notification.data.organizationSlug}/${notification.data.projectSlug}/opportunities/${notification.data.studyId}`
            : "/home",
      };

    case "STUDY_APPROVAL_REQUESTED": {
      // Default to overview tab for all organizations
      // External panel orgs have structured action.webPath that takes precedence
      return {
        type: NotificationActionType.NAVIGATE,
        webPath:
          notification.data?.organizationSlug &&
          notification.data?.projectSlug &&
          notification.data?.studyId
            ? `/${notification.data.organizationSlug}/${notification.data.projectSlug}/opportunities/${notification.data.studyId}`
            : "/home",
      };
    }

    default:
      return { type: NotificationActionType.DISMISS };
  }
}

/**
 * The panel is dark only where the surface that opens it is. The sidebar rail
 * paints itself with literal hex rather than a `.dark` class, so a menu that
 * portals out of it lands in a body still following the app theme and has to be
 * told. Every other caller (the marketing headers, the published-page header,
 * the internal top bar) sits on a light surface and must keep following it.
 * See `.theme-dark` in globals.css for why `dark` alone is not enough.
 */
const DARK_MENU_CLASS =
  "dark theme-dark border-[#1f1f1f] bg-[#0d0d0d] text-[#fafafa]";
const THEMED_MENU_CLASS = "border-border bg-background";

interface NotificationDropdownProps {
  buttonClassName?: string;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  /** Caption under the bell. See `NotificationIcon`. */
  label?: string;
  /**
   * Gap between the trigger and the panel. A trigger inset from its surface's
   * edge -- a tile inside a padded sidebar footer -- needs to add that inset
   * back, or a `side="right"` panel opens on top of the rail it came from.
   */
  sideOffset?: number;
  /** Hold the panel dark whatever the app theme is. For dark surfaces only. */
  darkMenu?: boolean;
}

export default function NotificationDropdown({
  buttonClassName,
  side,
  align = "end",
  label,
  sideOffset = 4,
  darkMenu = false,
}: NotificationDropdownProps) {
  const router = useRouter();
  const { triggerProps, contentProps } = useMenuFocusReturn();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"inbox" | "archive">("inbox");
  const [hoveredNotificationId, setHoveredNotificationId] = useState<
    string | null
  >(null);
  const utils = api.useUtils();

  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Get inbox notifications (non-archived) - only fetch when dropdown is open
  const { data: inboxNotificationsData, isLoading: isLoadingInbox } =
    api.notification.getNotifications.useQuery(
      { limit: 20, archived: false },
      {
        enabled: hasMounted && isOpen,
        refetchInterval: isOpen ? 30000 : false,
      },
    );

  // Get archived notifications - only fetch when dropdown is open AND archive tab is active
  const { data: archivedNotificationsData, isLoading: isLoadingArchive } =
    api.notification.getNotifications.useQuery(
      { limit: 20, archived: true },
      {
        enabled: hasMounted && isOpen && activeTab === "archive",
        refetchInterval: isOpen && activeTab === "archive" ? 30000 : false,
      },
    );

  const inboxNotifications: Notification[] =
    (inboxNotificationsData as Notification[] | undefined) ?? [];
  const archivedNotifications: Notification[] =
    (archivedNotificationsData as Notification[] | undefined) ?? [];

  const isLoading =
    (activeTab === "inbox" && isLoadingInbox) ||
    (activeTab === "archive" && isLoadingArchive);

  // Get unread count - always enabled for badge indicator, but only refetch when open
  const { data: unreadCount = 0 } = api.notification.getUnreadCount.useQuery(
    undefined,
    {
      enabled: hasMounted,
      refetchInterval: isOpen ? 30000 : 60000, // Slower polling when closed
    },
  );

  // Mutations
  const markAsReadMutation = api.notification.markAsRead.useMutation({
    onSettled: () => {
      void utils.notification.getNotifications.invalidate();
      void utils.notification.getUnreadCount.invalidate();
    },
  });

  const archiveNotificationMutation =
    api.notification.archiveNotification.useMutation({
      onMutate: async ({ notificationId }) => {
        await utils.notification.getNotifications.cancel();
        await utils.notification.getUnreadCount.cancel();

        const previousInboxNotifications =
          utils.notification.getNotifications.getData({
            limit: 20,
            archived: false,
          }) as Notification[] | undefined;
        const previousUnreadCount = utils.notification.getUnreadCount.getData();

        if (previousInboxNotifications) {
          const filtered = previousInboxNotifications.filter(
            (n) => n.id !== notificationId,
          );
          utils.notification.getNotifications.setData(
            { limit: 20, archived: false },
            filtered,
          );
        }

        if (previousUnreadCount !== undefined && previousInboxNotifications) {
          const archivedNotification = previousInboxNotifications.find(
            (n) => n.id === notificationId,
          );
          if (archivedNotification && !archivedNotification.read) {
            utils.notification.getUnreadCount.setData(
              undefined,
              Math.max(0, previousUnreadCount - 1),
            );
          }
        }

        return {
          previousInboxNotifications,
          previousUnreadCount,
        };
      },
      onError: (_err, _variables, context) => {
        const ctx = context;
        if (ctx?.previousInboxNotifications) {
          utils.notification.getNotifications.setData(
            { limit: 20, archived: false },
            ctx.previousInboxNotifications,
          );
        }
        if (ctx?.previousUnreadCount !== undefined) {
          utils.notification.getUnreadCount.setData(
            undefined,
            ctx.previousUnreadCount,
          );
        }
      },
      onSettled: () => {
        void utils.notification.getNotifications.invalidate();
        void utils.notification.getUnreadCount.invalidate();
      },
    });

  const archiveAllNotificationsMutation =
    api.notification.archiveAllNotifications.useMutation({
      onMutate: async () => {
        await utils.notification.getNotifications.cancel();
        await utils.notification.getUnreadCount.cancel();

        const previousInboxNotifications =
          utils.notification.getNotifications.getData({
            limit: 20,
            archived: false,
          }) as Notification[] | undefined;
        const previousUnreadCount = utils.notification.getUnreadCount.getData();

        utils.notification.getNotifications.setData(
          { limit: 20, archived: false },
          [],
        );
        utils.notification.getUnreadCount.setData(undefined, 0);

        return {
          previousInboxNotifications,
          previousUnreadCount,
        };
      },
      onError: (_err, _variables, context) => {
        const ctx = context;
        if (ctx?.previousInboxNotifications) {
          utils.notification.getNotifications.setData(
            { limit: 20, archived: false },
            ctx.previousInboxNotifications,
          );
        }
        if (ctx?.previousUnreadCount !== undefined) {
          utils.notification.getUnreadCount.setData(
            undefined,
            ctx.previousUnreadCount,
          );
        }
      },
      onSettled: () => {
        void utils.notification.getNotifications.invalidate();
        void utils.notification.getUnreadCount.invalidate();
      },
    });

  /**
   * Handle notification click using action-based routing
   */
  const handleNotificationClick = (notification: Notification) => {
    // Mark as read if not already
    if (!notification.read) {
      markAsReadMutation.mutate({ notificationId: notification.id });
    }

    const action = getNotificationAction(notification);

    switch (action.type) {
      case NotificationActionType.NAVIGATE:
        if (action.webPath) {
          // Add source parameter to track that user came from web notification
          const url = new URL(action.webPath, window.location.origin);
          url.searchParams.set("source", "web_notification");
          router.push(url.pathname + url.search);
        }
        setIsOpen(false);
        break;

      case NotificationActionType.EXTERNAL:
        if (action.webPath) {
          // Add UTM parameters and source parameter for external links
          const baseUrl = `${window.location.origin}${action.webPath}`;
          const urlParams = new URLSearchParams({
            utm_source: "internal",
            utm_medium: "notification",
            utm_campaign: notification.data?.studyId || "notification",
            utm_content: "in_app_notification",
            source: "web_notification",
          });
          window.location.href = `${baseUrl}?${urlParams.toString()}`;
        }
        setIsOpen(false);
        break;

      case NotificationActionType.DISMISS:
      default:
        setIsOpen(false);
        break;
    }
  };

  const handleArchiveNotification = (
    e: React.MouseEvent,
    notificationId: string,
  ) => {
    e.stopPropagation();
    archiveNotificationMutation.mutate({ notificationId });
  };

  const handleArchiveAll = () => {
    archiveAllNotificationsMutation.mutate();
  };

  const currentNotifications =
    activeTab === "inbox" ? inboxNotifications : archivedNotifications;

  if (!hasMounted) {
    // Hold the icon's exact footprint before mount so the bell doesn't pop in
    // and shove neighboring footer/header content sideways. Same markup as the
    // mounted trigger (minus the dropdown), so there's zero layout shift.
    return <NotificationIcon className={buttonClassName} label={label} />;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      {/* The bell itself, not a wrapper around it. Radix hands focus back to
          whatever the trigger renders when the panel closes, and a div takes the
          trigger props without being able to hold focus -- so the panel was
          neither reachable by keyboard nor able to give focus back. */}
      <DropdownMenuTrigger asChild {...triggerProps}>
        <NotificationIcon
          unreadCount={unreadCount}
          className={buttonClassName}
          label={label}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={cn(
          "w-96 rounded-lg p-0 shadow-lg",
          darkMenu ? DARK_MENU_CLASS : THEMED_MENU_CLASS,
        )}
        align={align}
        side={side}
        sideOffset={sideOffset}
        {...contentProps}
      >
        {/* Header with tabs and settings */}
        <div className="border-border border-b">
          <div className="relative px-2">
            <div className="flex items-center justify-between">
              <div className="flex">
                <div className="relative">
                  <button
                    onClick={() => {
                      setActiveTab("inbox");
                    }}
                    className={cn(
                      "px-4 py-3 text-sm font-medium transition-colors",
                      activeTab === "inbox"
                        ? "text-foreground"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    Inbox
                    {inboxNotifications.length > 0 && (
                      <span className="bg-accent ml-2 rounded-full px-2 py-0.5 text-xs select-none">
                        {inboxNotifications.length}
                      </span>
                    )}
                  </button>
                  {activeTab === "inbox" && (
                    <div className="absolute right-0 bottom-0 left-0 h-[2px] bg-blue-500" />
                  )}
                </div>
                <div className="relative">
                  <button
                    onClick={() => {
                      setActiveTab("archive");
                    }}
                    className={cn(
                      "px-4 py-3 text-sm font-medium transition-colors",
                      activeTab === "archive"
                        ? "text-foreground"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    Archive
                  </button>
                  {activeTab === "archive" && (
                    <div className="absolute right-0 bottom-0 left-0 h-[2px] bg-blue-500" />
                  )}
                </div>
              </div>
              <div className="flex items-center pr-2">
                <Link
                  href="/me/settings/notifications"
                  onClick={() => {
                    setIsOpen(false);
                  }}
                  className="hover:bg-accent inline-flex size-8 items-center justify-center rounded-full"
                >
                  <Settings className="size-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Notifications content */}
        {isLoading ? (
          /* eslint-disable react/no-array-index-key -- Static skeleton placeholders */
          <div className="p-2">
            {[...Array(4)].map((_, i) => (
              <div
                key={`skeleton-${i}`}
                className="border-border border-b p-4 last:border-b-0"
              >
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1 space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                  <Skeleton className="size-2 shrink-0 rounded-full" />
                </div>
              </div>
            ))}
          </div> /* eslint-enable react/no-array-index-key */
        ) : currentNotifications.length === 0 ? (
          <div className="text-muted-foreground p-4 text-center text-sm">
            {activeTab === "inbox"
              ? "No notifications yet"
              : "No archived notifications"}
          </div>
        ) : (
          <ScrollArea className="h-[400px]">
            {currentNotifications.map((notification) => (
              <div
                key={notification.id}
                className={cn(
                  "group border-border hover:bg-accent relative cursor-pointer border-b p-4 transition-colors last:border-b-0",
                  !notification.read && activeTab === "inbox" && "bg-accent/50",
                )}
                onClick={() => {
                  handleNotificationClick(notification);
                }}
                onMouseEnter={() => {
                  setHoveredNotificationId(notification.id);
                }}
                onMouseLeave={() => {
                  setHoveredNotificationId(null);
                }}
              >
                <div className="flex w-full items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="max-w-[290px] text-sm leading-5 font-medium break-words">
                      {renderNotificationContent(notification)}
                    </p>
                    <p className="text-muted-foreground mt-1 text-xs">
                      {formatDistanceToNow(new Date(notification.createdAt), {
                        addSuffix: true,
                      })}
                    </p>
                  </div>

                  {/* Status indicator and archive button */}
                  {activeTab === "inbox" && (
                    <div className="relative flex size-6 shrink-0 items-center justify-center">
                      {!notification.read && (
                        <div
                          className={cn(
                            "size-2 rounded-full bg-blue-500 transition-[translate,opacity] duration-100 ease-out",
                            hoveredNotificationId === notification.id &&
                              "translate-x-4 opacity-0",
                          )}
                        />
                      )}

                      <Button
                        variant="ghost"
                        size="sm"
                        className={cn(
                          "absolute right-0 size-6 p-0 transition-[translate,opacity] duration-100 ease-out",
                          hoveredNotificationId === notification.id
                            ? "translate-x-0 opacity-100"
                            : "pointer-events-none translate-x-4 opacity-0",
                        )}
                        onClick={(e) => {
                          handleArchiveNotification(e, notification.id);
                        }}
                      >
                        <Archive className="h-3 w-3" />
                      </Button>
                    </div>
                  )}

                  {activeTab === "archive" && (
                    <div className="flex shrink-0 items-center">
                      <Check className="text-muted-foreground size-4" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </ScrollArea>
        )}

        {/* Archive All button */}
        {activeTab === "inbox" &&
          inboxNotifications.length > 0 &&
          !isLoading && (
            <div className="border-border border-t p-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleArchiveAll}
                className="w-full text-xs"
                disabled={archiveAllNotificationsMutation.isPending}
              >
                Archive All
              </Button>
            </div>
          )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
